import { Link } from "react-router-dom";
import { Heart, Music, Presentation, Wine, ClipboardList, CheckCircle2, ArrowRight } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const IMG = {
  stage: "https://images.unsplash.com/photo-1762968269894-1d7e1ce8894e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  launch: "https://images.unsplash.com/photo-1762968274962-20c12e6e8ecd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  gala: "https://images.unsplash.com/photo-1653821355736-0c2598d0a63e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  concert: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  wedding: "https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const SERVICES = [
  {
    icon: Heart,
    title: "Weddings & Engagements",
    desc: "Turning your dream day into reality. We handle complete wedding planning, pre-wedding ceremonies (Sangeet, Mehendi), thematic decor, catering, and guest management for a completely stress-free celebration.",
    link: { label: "Looking for a getaway? Explore our Destination Weddings division", to: "/destination-weddings" },
  },
  {
    icon: Music,
    title: "Live Concerts & Musical Nights",
    desc: "End-to-end production for singer concerts, live bands, and cultural nights. We manage stage fabrication, professional sound/light engineering (AV), artist hospitality, ticketing, and crowd control.",
  },
  {
    icon: Presentation,
    title: "Corporate Conferences & Product Launches",
    desc: "High-impact execution for annual general meetings, brand activations, and industry summits designed to maximize engagement and media coverage.",
  },
  {
    icon: Wine,
    title: "Private Parties & Milestone Celebrations",
    desc: "Elegant planning for grand birthdays, milestone anniversaries, and exclusive VIP social gatherings with bespoke themes and premium hospitality.",
  },
  {
    icon: ClipboardList,
    title: "Turnkey Vendor Coordination",
    desc: "We act as your single point of contact, seamlessly coordinating decorators, security teams, technical crews, and caterers under one unified Evolve Elite manager.",
  },
];

const PROCESS = [
  { n: "01", t: "Conceptualize", d: "Objectives, audience, theme and budget locked in one briefing." },
  { n: "02", t: "Plan", d: "Venue, vendors, run-of-show and contingencies — documented to the minute." },
  { n: "03", t: "Produce", d: "Stage, AV, décor and fabrication built and rehearsed before doors open." },
  { n: "04", t: "Execute", d: "One Evolve Elite manager on the ground; you focus on your guests." },
];

const EVENT_SERVICES = [
  "Weddings & Engagements",
  "Live Concerts & Musical Nights",
  "Corporate Conferences & Product Launches",
  "Private Parties & Milestone Celebrations",
  "Turnkey Vendor Coordination",
];

export default function Events() {
  return (
    <div data-testid="events-page">
      <PageHero
        eyebrow="Corporate · Personal · Entertainment"
        title={["Comprehensive", "Event Management."]}
        sub="Flawless execution requires strategic planning, creative vision, and an obsession with detail. Evolve Elite provides end-to-end event management services across the entire spectrum of corporate, personal, and entertainment events. From conceptualization to on-ground execution, we transform your ideas into immersive, seamless experiences, allowing you to simply show up and enjoy the moment."
      >
        <ScrollCta href="#proposal" label="Request an Event Proposal" testId="events-proposal-btn" />
      </PageHero>

      {/* FEATURE IMAGE + SERVICES */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="events-services-section">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl" data-testid="events-feature-image">
              <img src={IMG.stage} alt="Corporate conference stage" className="h-[320px] w-full object-cover sm:h-[460px]" />
              <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/40 to-transparent" />
              <div className="absolute inset-y-0 left-0 flex max-w-xl flex-col justify-end p-8 sm:p-12">
                <p className="eyebrow !text-gold">Corporate · Personal · Entertainment</p>
                <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                  From concept to curtain call.
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7" data-testid={`events-service-${i + 1}`}>
                  <s.icon className="h-8 w-8 text-amber-600" />
                  <h3 className="mt-6 font-display text-xl font-extrabold text-navy">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  {s.link && (
                    <Link
                      to={s.link.to}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-amber-600 transition-all hover:gap-2.5"
                      data-testid="events-weddings-link"
                    >
                      {s.link.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="grain flex h-full flex-col justify-between rounded-3xl bg-navy p-7">
                <CheckCircle2 className="h-8 w-8 text-gold" />
                <div>
                  <h3 className="mt-8 font-display text-xl font-extrabold text-white">One manager. Zero loose ends.</h3>
                  <p className="mt-2 text-sm text-white/65">
                    Every vendor, crew and timeline reports to a single Evolve Elite event lead who is accountable to
                    you — before, during and after the event.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROCESS + GALLERY */}
      <section className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-24" data-testid="events-process-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">How We Deliver</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Conceptualize. Plan. Produce. Execute.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7">
                  <span className="font-display text-4xl font-black text-gold/40">{p.n}</span>
                  <h3 className="mt-4 font-display text-lg font-extrabold text-navy">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { img: IMG.wedding, label: "Wedding & Sangeet" },
              { img: IMG.concert, label: "Live Concert Night" },
              { img: IMG.launch, label: "Product Launch" },
              { img: IMG.gala, label: "Milestone Celebration" },
            ].map((g, i) => (
              <Reveal key={g.label} delay={i * 0.06}>
                <div className="group relative overflow-hidden rounded-3xl" data-testid={`events-gallery-${i + 1}`}>
                  <img
                    src={g.img}
                    alt={g.label}
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent p-5 pt-14">
                    <p className="font-display text-lg font-extrabold text-white">{g.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROPOSAL FORM */}
      <section id="proposal" className="py-16 sm:py-24" data-testid="events-proposal-section">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Event Proposal</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Request an Event Proposal.
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              Wedding, concert, conference or celebration — share the occasion, date and headcount. We return a concept,
              a venue shortlist and a transparent budget, with one Evolve Elite manager named from day one.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="events-proposal-form-card">
              <LeadForm
                source="events-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={EVENT_SERVICES}
                cta="Request an Event Proposal"
                idPrefix="events-proposal"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
